import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  OnDestroy,
  output,
  signal,
  untracked,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { marker as _ } from '@vandres/ngx-translate-extract-marker';
import { FaDuotoneIconComponent, FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faChartSimple, faXmark } from '@fortawesome/pro-duotone-svg-icons';
import {
  faCopy,
  faCircleExclamation,
  faDisplay,
  faTriangleExclamation,
  faVideo,
} from '@fortawesome/pro-regular-svg-icons';
import { faCheck, faExclamation, faXmark as faXmarkSolid } from '@fortawesome/pro-solid-svg-icons';
import { UserStore } from '../../../state/user.repository';
import { OdinService } from '../../../core/services/odin.service';
import {
  StatsTarget,
  StreamStats as StreamStatsSample,
  StreamStatsService,
} from '../../../core/services/stream-stats.service';
import {
  StreamHealth,
  StreamHealthService,
  StreamKind,
  streamKey,
} from '../../../core/services/stream-health.service';
import { StatsGraph } from '../../../shared/components/stats-graph/stats-graph';
import { Button } from '../../../shared/components/ui/buttons/gradient-btn/button';
import { LanguageService } from '../../../core/services/language.service';

/** Samples kept per graph: one minute at one sample per second. */
const HISTORY = 60;
/** Ping above this is marked in the graph. */
const RTT_WARN_MS = 150;
/** Loss above these is marked in the graph. */
const LOSS_WARN_PCT = 1;
const LOSS_ERROR_PCT = 5;

interface Tab {
  kind: StreamKind;
  target: StatsTarget;
}

interface History {
  bitrate: number[];
  fps: number[];
  rtt: number[];
  loss: number[];
  /** Encoder cap next to the bitrate, drawn only while it binds. */
  cap: number[];
}

type StepStatus = 'done' | 'warn' | 'failed' | 'active' | 'pending';

/** One node of the lifecycle strip. */
interface Step {
  label: string;
  status: StepStatus;
  details: { label: string; value: string }[];
}

/**
 * Stats of one user's video streams, with a tab per stream (camera, screen
 * share). Shows the lifecycle of the stream from subscription to decoding
 * (or capture to server acknowledgment for the own streams), a verdict on
 * where a problem most likely sits, one-minute graphs and the raw numbers.
 * Values a platform doesn't report render as "–".
 */
@Component({
  selector: 'andor-stream-stats',
  imports: [TranslatePipe, FaIconComponent, FaDuotoneIconComponent, StatsGraph, Button],
  templateUrl: './stream-stats.html',
  styleUrl: './stream-stats.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'closed.emit()',
  },
})
export class StreamStats implements OnDestroy {
  private readonly userStore = inject(UserStore);
  private readonly odin = inject(OdinService);
  private readonly statsService = inject(StreamStatsService);
  private readonly healthService = inject(StreamHealthService);
  private readonly translate = inject(TranslateService);
  // Read before `instant()` so labels re-translate when the language changes.
  private readonly lang = inject(LanguageService).active;

  userId = input.required<string>();
  /** The tab to open on, e.g. the stream a tile's warning badge belongs to. */
  initialKind = input<StreamKind>('cam');
  closed = output<void>();

  protected readonly faChartSimple = faChartSimple;
  protected readonly faXmark = faXmark;
  protected readonly faCheck = faCheck;
  protected readonly faCopy = faCopy;
  protected readonly faXmarkSolid = faXmarkSolid;
  protected readonly faExclamation = faExclamation;
  protected readonly faCircleExclamation = faCircleExclamation;
  protected readonly faTriangleExclamation = faTriangleExclamation;
  protected readonly faDisplay = faDisplay;
  protected readonly faVideo = faVideo;

  protected readonly user = toSignal(
    toObservable(this.userId).pipe(switchMap((id) => this.userStore.get$(id)))
  );

  // Compared by stream identity: other user updates (speaking, raised hand)
  // must not restart the watched streams' lifecycle.
  protected readonly tabs = computed<Tab[]>(
    () => {
      const user = this.user();
      const tabs: Tab[] = [];
      if (user?.screenCastStream) {
        tabs.push({ kind: 'screen', target: this.odin.videoStatsTarget(user.screenCastStream) });
      }
      if (user?.camStream) {
        tabs.push({ kind: 'cam', target: this.odin.videoStatsTarget(user.camStream) });
      }
      return tabs;
    },
    {
      equal: (a, b) =>
        a.length === b.length &&
        a.every(
          (tab, i) =>
            tab.kind === b[i].kind &&
            tab.target.mediaId === b[i].target.mediaId &&
            tab.target.trackId === b[i].target.trackId
        ),
    }
  );

  protected readonly selectedKind = linkedSignal(() => this.initialKind());
  protected readonly activeKind = computed<StreamKind | null>(() => {
    const tabs = this.tabs();
    return (tabs.find((t) => t.kind === this.selectedKind()) ?? tabs[0])?.kind ?? null;
  });

  protected readonly health = computed<StreamHealth | null>(() => {
    const kind = this.activeKind();
    const key = kind && streamKey(this.userId(), kind);
    return this.healthService.streams().find((s) => s.key === key)?.health ?? null;
  });
  protected readonly sourceOk = this.healthService.sourceOk;

  private readonly samples = signal<Partial<Record<StreamKind, StreamStatsSample>>>({});
  private readonly histories = signal<Partial<Record<StreamKind, History>>>({});

  protected readonly stats = computed(() => {
    const kind = this.activeKind();
    return kind ? (this.samples()[kind] ?? null) : null;
  });
  protected readonly history = computed(() => {
    const kind = this.activeKind();
    return (kind && this.histories()[kind]) || emptyHistory();
  });

  protected readonly copied = signal(false);
  private copiedTimer: ReturnType<typeof setTimeout> | undefined;
  private readonly timer = setInterval(() => void this.poll(), 1000);

  constructor() {
    // The streams stay watched while the dialog shows them, so the health
    // keeps updating even when their tiles scroll out of view.
    effect((onCleanup) => {
      const userId = this.userId();
      const tabs = this.tabs();
      untracked(() => {
        for (const tab of tabs) this.healthService.watch(userId, tab.kind, tab.target);
      });
      onCleanup(() => {
        for (const tab of tabs) this.healthService.unwatch(userId, tab.kind);
      });
    });
    // Close once the user stopped all streams or left.
    let loaded = false;
    effect(() => {
      const user = this.user();
      loaded ||= !!user;
      if (loaded && (!user || this.tabs().length === 0)) {
        untracked(() => this.closed.emit());
      }
    });
    void this.poll();
  }

  ngOnDestroy(): void {
    clearInterval(this.timer);
    clearTimeout(this.copiedTimer);
  }

  protected selectTab(kind: StreamKind): void {
    this.selectedKind.set(kind);
  }

  protected readonly isSelf = computed(() => !!this.user()?.isSelf);

  protected readonly chips = computed(() => {
    this.lang();
    const s = this.stats();
    const h = this.health();
    const chips: { text: string; kind: 'codec' | 'plain' | 'warn' }[] = [];
    const codec = s?.codec ?? h?.codec;
    if (codec) chips.push({ text: codec, kind: 'codec' });
    const width = s?.width ?? h?.width;
    const height = s?.height ?? h?.height;
    if (width && height) chips.push({ text: `${width}×${height}`, kind: 'plain' });
    const limitation = s?.qualityLimitation ?? h?.qualityLimitation;
    if (limitation && limitation !== 'none') {
      chips.push({
        text: this.translate.instant(_('Limited by [reason]'), { reason: limitation }),
        kind: 'warn',
      });
    }
    return chips;
  });

  protected readonly verdict = computed<{
    severity: 'info' | 'warn' | 'error';
    key: string;
    params: Record<string, unknown>;
  } | null>(() => {
    const h = this.health();
    if (h?.state === 'untracked') {
      return {
        severity: 'info',
        key: _(
          "This browser doesn't report statistics for your stream, so its health can't be checked."
        ),
        params: {},
      };
    }
    if (!h || (h.severity !== 'warn' && h.severity !== 'error')) return null;
    const severity = h.severity;
    const secs = Math.round((h.lastPacketAgoMs ?? h.sinceMs) / 1000);
    const name = this.user()?.data.name ?? '';
    const key = h.direction === 'send' ? sendVerdict(h) : receiveVerdict(h);
    return key ? { severity, key, params: { secs, name } } : null;
  });

  protected readonly steps = computed<Step[]>(() => {
    const h = this.health();
    // Without stats there's no lifecycle to show: the steps would sit on
    // "nothing yet" next to a verdict saying only the numbers are missing.
    if (!h || h.state === 'untracked') return [];
    return h.direction === 'send' ? sendSteps(h) : receiveSteps(h);
  });

  /** Whether the edge before step `i` connects two completed steps. */
  protected edgeOk(steps: Step[], i: number): boolean {
    const done = (s: StepStatus) => s === 'done' || s === 'warn';
    return done(steps[i - 1].status) && done(steps[i].status);
  }

  protected readonly graphs = computed(() => {
    const s = this.stats();
    const h = this.history();
    const capBinding = capBinds(s);
    return [
      {
        label: _('Bitrate (Kbps)'),
        series: h.bitrate,
        cap: capBinding ? h.cap : undefined,
        digits: 0,
        tone: 'ok' as const,
      },
      { label: _('FPS'), series: h.fps, digits: 0, tone: 'ok' as const },
      {
        label: _('RTT (ms)'),
        series: h.rtt,
        digits: 0,
        tone: (s?.rttMs ?? 0) > RTT_WARN_MS ? ('warn' as const) : ('ok' as const),
      },
      {
        label: _('Loss (%)'),
        series: h.loss,
        digits: 1,
        tone:
          (s?.lossPct ?? 0) > LOSS_ERROR_PCT
            ? ('error' as const)
            : (s?.lossPct ?? 0) > LOSS_WARN_PCT
              ? ('warn' as const)
              : ('ok' as const),
      },
    ];
  });

  protected readonly rows = computed(() => {
    const s = this.stats();
    const h = this.health();
    if (!s) return [];
    const dash = (v: string | number | null | undefined, unit = '') =>
      v === undefined || v === null ? '–' : `${v}${unit}`;
    const rows: { k: string; v: string }[] =
      s.direction === 'send'
        ? [{ k: _('Encoder'), v: dash(s.encoder) }]
        : [{ k: _('Decoder'), v: dash(s.decoder) }];
    if (capBinds(s))
      rows.push({ k: _('Bandwidth Cap'), v: `${s.bweCapKbps} / ${s.bweMaxKbps} Kbps` });
    rows.push(
      { k: _('Jitter'), v: dash(s.jitterMs, ' ms') },
      { k: _('Keyframes/s'), v: dash(s.keyframesPerSec) },
      { k: _('PLI / NACK'), v: `${dash(s.pli)} / ${dash(s.nack)}` }
    );
    if (s.direction === 'receive') {
      rows.push(
        { k: _('Packets Received'), v: dash(h?.packetsPerSec, ' /s') },
        { k: _('Dropped Frames'), v: dash(s.droppedFrames) },
        { k: _('Freezes (60 s)'), v: dash(h?.freezes60s) }
      );
    } else {
      rows.push(
        { k: _('Packets Sent'), v: dash(h?.packetsPerSec, ' /s') },
        { k: _('Retransmitted'), v: dash(s.retransmittedKb, ' KB') }
      );
    }
    return rows;
  });

  protected async copyDiagnostics(): Promise<void> {
    const user = this.user();
    const diagnostics = {
      userId: this.userId(),
      peerId: user?.peerId,
      isSelf: user?.isSelf,
      platform: navigator.userAgent,
      streams: this.tabs().map((tab) => ({
        kind: tab.kind,
        target: { ...tab.target, stream: undefined },
        stats: this.samples()[tab.kind] ?? null,
        health: this.healthService.health(this.userId(), tab.kind),
      })),
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(diagnostics, null, 2));
    } catch {
      return;
    }
    this.copied.set(true);
    clearTimeout(this.copiedTimer);
    this.copiedTimer = setTimeout(() => this.copied.set(false), 2000);
  }

  /** Samples every tab, so switching tabs shows a warm graph. */
  private async poll(): Promise<void> {
    const tabs = this.tabs();
    const samples = await Promise.all(tabs.map((tab) => this.statsService.sample(tab.target)));
    const nextSamples: Partial<Record<StreamKind, StreamStatsSample>> = {};
    const nextHistories = { ...this.histories() };
    tabs.forEach((tab, i) => {
      const sample = samples[i];
      if (!sample) return;
      nextSamples[tab.kind] = sample;
      const h = nextHistories[tab.kind] ?? emptyHistory();
      nextHistories[tab.kind] = {
        bitrate: push(h.bitrate, sample.bitrateKbps ?? 0),
        fps: push(h.fps, sample.fps ?? 0),
        rtt: push(h.rtt, sample.rttMs ?? 0),
        loss: push(h.loss, sample.lossPct ?? 0),
        cap: push(h.cap, sample.bweCapKbps ?? 0),
      };
    });
    this.samples.set(nextSamples);
    this.histories.set(nextHistories);
  }
}

function emptyHistory(): History {
  return { bitrate: [], fps: [], rtt: [], loss: [], cap: [] };
}

function push(series: number[], value: number): number[] {
  const next = [...series, value];
  return next.length > HISTORY ? next.slice(-HISTORY) : next;
}

/** True while the bandwidth estimate holds the encoder below its ceiling. */
function capBinds(s: StreamStatsSample | null): boolean {
  return s?.bweCapKbps !== undefined && s.bweMaxKbps !== undefined && s.bweCapKbps < s.bweMaxKbps;
}

function receiveVerdict(h: StreamHealth): string | null {
  switch (h.state) {
    case 'no-media':
      return _(
        "No video data from [name] is arriving. This usually points to a network problem on the sender's side."
      );
    case 'waiting-keyframe':
      return _("Data is arriving but playback hasn't started yet — waiting for a keyframe.");
    case 'stalled':
      return _('The stream stopped — nothing has arrived for [secs] seconds.');
    case 'failed':
      return _('Data is arriving but nothing can be decoded.');
    case 'degraded':
      return _(
        "Packet loss or freezes are reducing this stream. If your own connection looks fine, the problem is likely on the sender's side."
      );
    default:
      return null;
  }
}

function sendVerdict(h: StreamHealth): string | null {
  switch (h.state) {
    case 'no-capture':
      return _("Your video source isn't producing frames — the capture or encoder failed.");
    case 'no-ack':
      return _(
        "The server doesn't acknowledge your stream — your network may be blocking outgoing video."
      );
    case 'stalled':
      return _('Your stream stopped — nothing has been sent for [secs] seconds.');
    case 'degraded':
      return h.qualityLimitation === 'cpu'
        ? _(
            'Your device is too busy to encode at full quality, so others see you with less detail.'
          )
        : _('Your upload limits this stream, so others see you with less detail.');
    default:
      return null;
  }
}

function receiveSteps(h: StreamHealth): Step[] {
  const gotPacket = h.firstPacketAgoMs !== null;
  const gotFrame = h.firstFrameAgoMs !== null;
  const firstPacketAfter =
    h.firstPacketAgoMs !== null ? fmtDuration(h.watchedForMs - h.firstPacketAgoMs) : '–';
  return [
    {
      label: _('Subscribed'),
      status: 'done',
      details: [{ label: _('Watching for'), value: fmtDuration(h.watchedForMs) }],
    },
    {
      label: _('Announced'),
      status: h.announced ? 'done' : h.state === 'connecting' ? 'active' : 'failed',
      details: [{ label: _('Slot'), value: h.announced ? _('Confirmed') : _('Not announced') }],
    },
    {
      label: _('First Packet'),
      status: gotPacket
        ? h.state === 'stalled'
          ? 'failed'
          : 'done'
        : h.state === 'connecting'
          ? 'active'
          : 'failed',
      details: gotPacket
        ? [
            { label: _('Arrived after'), value: firstPacketAfter },
            {
              label: _('Current rate'),
              value: h.packetsPerSec !== null ? `${h.packetsPerSec} /s` : '–',
            },
            { label: _('Last packet'), value: fmtAgo(h.lastPacketAgoMs) },
          ]
        : [{ label: _('Received'), value: _('Nothing yet') }],
    },
    {
      label: _('Decoding'),
      status: gotFrame
        ? h.state === 'failed'
          ? 'failed'
          : h.state === 'degraded'
            ? 'warn'
            : 'done'
        : gotPacket
          ? h.severity === 'warn'
            ? 'warn'
            : 'active'
          : 'pending',
      details: [
        { label: _('Rate'), value: h.fps !== null ? `${h.fps} fps` : '–' },
        { label: _('Last frame'), value: fmtAgo(h.lastFrameAgoMs) },
        ...(h.keyframeRequests !== null
          ? [{ label: _('Keyframe requests'), value: `${h.keyframeRequests}` }]
          : []),
      ],
    },
  ];
}

function sendSteps(h: StreamHealth): Step[] {
  const gotFrame = h.firstFrameAgoMs !== null;
  const gotPacket = h.firstPacketAgoMs !== null;
  const gotAck = h.ackAgeMs !== null;
  const limited = h.qualityLimitation === 'bandwidth' || h.qualityLimitation === 'cpu';
  return [
    {
      label: _('Capturing'),
      status: gotFrame ? 'done' : h.state === 'connecting' ? 'active' : 'failed',
      details: [
        { label: _('First frame'), value: fmtAgo(h.firstFrameAgoMs) },
        ...(h.width && h.height ? [{ label: _('Size'), value: `${h.width}×${h.height}` }] : []),
      ],
    },
    {
      label: _('Encoding'),
      status: gotFrame ? (limited ? 'warn' : 'done') : 'pending',
      details: [
        { label: _('Encoder'), value: h.encoder ?? '–' },
        { label: _('Rate'), value: h.fps !== null ? `${h.fps} fps` : '–' },
        ...(limited ? [{ label: _('Limited by'), value: h.qualityLimitation ?? '' }] : []),
      ],
    },
    {
      label: _('Sending'),
      status: gotPacket
        ? h.state === 'stalled'
          ? 'failed'
          : 'done'
        : gotFrame
          ? h.state === 'connecting'
            ? 'active'
            : 'failed'
          : 'pending',
      details: [
        {
          label: _('Current rate'),
          value: h.packetsPerSec !== null ? `${h.packetsPerSec} /s` : '–',
        },
        { label: _('Last packet'), value: fmtAgo(h.lastPacketAgoMs) },
      ],
    },
    {
      label: _('Server Ack'),
      status: h.state === 'no-ack' ? 'failed' : gotAck ? 'done' : gotPacket ? 'active' : 'pending',
      details: [
        { label: _('Last report'), value: fmtAgo(h.ackAgeMs) },
        ...(h.ackRttMs !== null ? [{ label: _('RTT'), value: `${h.ackRttMs} ms` }] : []),
        ...(h.recentLossPct !== null
          ? [{ label: _('Reported loss'), value: `${h.recentLossPct} %` }]
          : []),
      ],
    },
  ];
}

/** Compact language-neutral duration: "0.4 s", "12 s", "3 min". */
function fmtDuration(ms: number): string {
  if (ms < 10_000) return `${Math.round(ms / 100) / 10} s`;
  if (ms < 120_000) return `${Math.round(ms / 1000)} s`;
  return `${Math.round(ms / 60_000)} min`;
}

function fmtAgo(ms: number | null): string {
  return ms === null ? '–' : `−${fmtDuration(ms)}`;
}
